import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  User, 
  Package,
  DollarSign,
  CheckCircle,
  ArrowLeft,
  Search,
  Filter,
  X,
  Save,
  Copy
} from 'lucide-react';

// Componentes reutilizables
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import ClientSearchSelect from '../components/common/ClientSearchSelect';

// Servicios
import clientService from '../services/clientService';
import serviceService from '../services/serviceService';
import serviceCategoryService from '../services/serviceCategoryService';
import salesService from '../services/salesService';

const NewSalePage = () => {
  const { id } = useParams(); // ID para modo edición
  const location = useLocation();
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  
  // Obtener parámetros de consulta
  const queryParams = new URLSearchParams(location.search);
  const cloneFromId = queryParams.get('clone_from');

  // Estados principales
  const [selectedClient, setSelectedClient] = useState(null);
  const [cart, setCart] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  
  // Estados de filtros para servicios
  const [serviceFilters, setServiceFilters] = useState({
    search: '',
    category: '',
    priceRange: ''
  });
  
  // Estados para categorías
  const [categories, setCategories] = useState([]);
  
  // Estados de paginación para servicios
  const [servicePagination, setServicePagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });
  
  // Estados del formulario
  const [saleData, setSaleData] = useState({
    notes: '',
    status: 'PENDING', // Forzado a PENDING por defecto
    paymentMethod: 'CASH'
  });

  // Función para normalizar texto (quitar tildes y convertir a minúsculas)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Quitar tildes
      .trim();
  };

  // Cargar servicios
  const loadServices = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await serviceService.getServices({
        page,
        limit: servicePagination.itemsPerPage,
        isActive: true,
        search: normalizeText(serviceFilters.search),
        category: serviceFilters.category
      });
      setServices(response.services || []);
      setServicePagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: response.pagination?.totalPages || 1,
        totalItems: response.pagination?.totalItems || 0
      }));
    } catch (error) {
      console.error('Error cargando servicios:', error);
      showNotification('Error al cargar los servicios', 'error');
    } finally {
      setLoading(false);
    }
  }, [serviceFilters, servicePagination.itemsPerPage, showNotification]);

  // Cargar clientes
  const loadClients = useCallback(async () => {
    try {
      const response = await clientService.getClients({
        page: 1,
        limit: 100,
        isActive: true
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  }, []);

  // Cargar categorías
  const loadCategories = useCallback(async () => {
    try {
      const response = await serviceCategoryService.getCategories({
        page: 1,
        limit: 100,
        isActive: true
      });
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  }, []);

  // Cargar datos de la venta (para edición o clonación)
  const loadSaleData = useCallback(async (saleId, mode) => {
    if (!saleId) return;

    try {
      if (mode === 'edit') setIsEditing(true);
      if (mode === 'clone') setIsCloning(true);

      const response = await salesService.getSaleById(saleId);
      const sale = response.sale;

      // Establecer cliente
      setSelectedClient(sale.client);

      // Establecer carrito
      const cartItems = sale.items.map(item => ({
        serviceId: item.service ? item.service._id : `custom-ai-${item._id || Math.random().toString(36).substr(2, 9)}`,
        name: item.service ? item.service.name : (item.description || 'Venta Personalizada IA'),
        price: item.unitPrice,
        category: item.service?.category?.name || 'Venta Personalizada IA',
        quantity: item.quantity,
        isCustomItem: !item.service
      }));
      setCart(cartItems);

      // Establecer datos adicionales
      // NOTA IMPORTANTE: No seteamos el estado aquí si es clonación
      // Si es edición, mantenemos el estado PENDING (asumimos que no se edita confirmado)
      setSaleData({
        notes: sale.notes || '',
        status: 'PENDING', // Siempre PENDING al cargar para editar/clonar en este flujo
        paymentMethod: mode === 'clone' ? 'CASH' : (sale.paymentMethod || 'CASH')
      });

      if (mode === 'clone') {
        showNotification('Datos de la venta cargados para clonación', 'success');
      }

    } catch (error) {
      console.error(`Error cargando venta para ${mode}:`, error);
      showNotification(`Error al cargar la venta para ${mode === 'clone' ? 'clonación' : 'edición'}`, 'error');
      navigate('/sales');
    }
  }, [navigate, showNotification]);

  // Cargar datos iniciales
  useEffect(() => {
    loadServices();
    loadClients();
    loadCategories();
    
    if (id) {
      loadSaleData(id, 'edit');
    } else if (cloneFromId) {
      loadSaleData(cloneFromId, 'clone');
    }
  }, [loadServices, loadClients, loadCategories, loadSaleData, id, cloneFromId]);

  // Recargar servicios cuando cambien los filtros
  useEffect(() => {
    loadServices(1);
  }, [serviceFilters, loadServices]);

  // Agregar servicio al carrito
  const addToCart = (service) => {
    const existingItem = cart.find(item => item.serviceId === service._id);
    
    if (existingItem) {
      // Si ya existe, aumentar cantidad
      setCart(prev => prev.map(item => 
        item.serviceId === service._id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      // Si no existe, agregar nuevo
      setCart(prev => [...prev, {
        serviceId: service._id,
        name: service.name,
        price: service.price,
        category: service.category?.name || 'Sin categoría',
        quantity: 1
      }]);
    }
    
    showNotification(`${service.name} agregado al carrito`, 'success');
  };

  // Remover servicio del carrito
  const removeFromCart = (serviceId) => {
    setCart(prev => prev.filter(item => item.serviceId !== serviceId));
    showNotification('Servicio removido del carrito', 'info');
  };

  // Actualizar cantidad en el carrito
  const updateQuantity = (serviceId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(serviceId);
      return;
    }
    
    setCart(prev => prev.map(item => 
      item.serviceId === serviceId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  // Calcular total del carrito
  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Manejar filtros de servicios
  const handleServiceFilterChange = (filterType, value) => {
    setServiceFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setServicePagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Manejar paginación de servicios
  const handleServicePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= servicePagination.totalPages) {
      loadServices(newPage);
    }
  };

  const goToPreviousServicePage = () => {
    if (servicePagination.currentPage > 1) {
      handleServicePageChange(servicePagination.currentPage - 1);
    }
  };

  const goToNextServicePage = () => {
    if (servicePagination.currentPage < servicePagination.totalPages) {
      handleServicePageChange(servicePagination.currentPage + 1);
    }
  };

  // Limpiar filtros de servicios
  const clearServiceFilters = () => {
    setServiceFilters({
      search: '',
      category: '',
      priceRange: ''
    });
  };

  // Procesar venta (Crear o Actualizar)
  const processSale = async () => {
    if (!selectedClient) {
      showNotification('Debes seleccionar un cliente', 'error');
      return;
    }
    
    if (cart.length === 0) {
      showNotification('El carrito está vacío', 'error');
      return;
    }

    try {
      const saleDataToSend = {
        client: selectedClient._id,
        items: cart.map(item => ({
          service: item.isCustomItem ? null : item.serviceId,
          description: item.isCustomItem ? item.name : undefined,
          quantity: item.quantity,
          unitPrice: item.price
        })),
        notes: saleData.notes,
        status: 'PENDING', // SIEMPRE PENDING al guardar desde aquí
        paymentMethod: saleData.paymentMethod
      };

      if (isEditing) {
        await salesService.updateSale(id, saleDataToSend);
        showNotification('Venta actualizada exitosamente', 'success');
      } else {
        // Modo crear o clonar (crear nuevo)
        await salesService.createSale(saleDataToSend);
        showNotification('Venta procesada exitosamente', 'success');
      }
      
      // Limpiar carrito y volver a la lista
      setCart([]);
      setSelectedClient(null);
      setSaleData({ notes: '', status: 'PENDING', paymentMethod: 'CASH' });
      navigate('/sales');
    } catch (error) {
      console.error('Error procesando venta:', error);
      showNotification(error.message || 'Error al procesar la venta', 'error');
    }
  };

  // Vaciar carrito
  const clearCart = () => {
    setCart([]);
    showNotification('Carrito vaciado', 'info');
  };

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(isEditing ? `/sales/${id}` : '/sales')}
            className="btn btn-outline flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Volver
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <ShoppingCart className="text-green-600" size={32} />
              {isEditing ? 'Editar Venta' : (isCloning ? 'Clonar Venta' : 'Nueva Venta')}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditing 
                ? 'Modifica los servicios y datos de la orden' 
                : (isCloning ? 'Crea una copia basada en una venta existente' : 'Selecciona servicios y crea la orden de venta')}
            </p>
          </div>
        </div>
        
        {cart.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {cart.length} servicio{cart.length !== 1 ? 's' : ''} en el carrito
            </span>
            <button
              onClick={clearCart}
              className="btn btn-outline btn-sm"
            >
              <Trash2 size={16} />
              Vaciar
            </button>
          </div>
        )}
      </div>

      <div className="new-sale-layout">
        {/* Panel Izquierdo - Servicios */}
        <div className="services-section">
          <div className="card h-full flex flex-col">
            <div className="card-header flex-shrink-0">
              <h3 className="card-title flex items-center gap-2">
                <Package className="text-blue-600" size={20} />
                Servicios Disponibles
              </h3>
            </div>
            
            <div className="card-content services-panel" style={{ 
              display: 'flex', 
              flexDirection: 'column'
            }}>
              {/* Filtros de servicios */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="filters-horizontal">
                  {/* Búsqueda */}
                  <div className="search-field">
                    <label className="form-label">Buscar servicio</label>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={serviceFilters.search}
                        onChange={(e) => handleServiceFilterChange('search', e.target.value)}
                        placeholder="Nombre del servicio..."
                        className="form-input pl-10"
                      />
                    </div>
                  </div>

                  {/* Filtro por categoría */}
                  <div className="filter-field">
                    <CustomSelect
                      label="Categoría"
                      value={serviceFilters.category}
                      onChange={(value) => handleServiceFilterChange('category', value)}
                      options={[
                        { value: '', label: 'Todas las categorías' },
                        ...categories.map(cat => ({ value: cat._id, label: cat.name }))
                      ]}
                      placeholder="Todas las categorías"
                    />
                  </div>

                  {/* Filtro por precio */}
                  <div className="filter-field">
                    <CustomSelect
                      label="Rango de precio"
                      value={serviceFilters.priceRange}
                      onChange={(value) => handleServiceFilterChange('priceRange', value)}
                      options={[
                        { value: '', label: 'Todos los precios' },
                        { value: '0-50000', label: '$0 - $50,000' },
                        { value: '50000-100000', label: '$50,000 - $100,000' },
                        { value: '100000-500000', label: '$100,000 - $500,000' },
                        { value: '500000+', label: 'Más de $500,000' }
                      ]}
                      placeholder="Todos los precios"
                    />
                  </div>

                  {/* Botón limpiar filtros */}
                  <div className="clear-button">
                    <label className="form-label">&nbsp;</label>
                    <button
                      onClick={clearServiceFilters}
                      className="btn btn-outline w-full"
                    >
                      <X size={16} />
                      Limpiar
                    </button>
                  </div>
                </div>
                
                {/* Selector de servicios por página */}
                <div className="services-per-page-selector">
                  <label>Servicios por página:</label>
                  <select
                    value={servicePagination.itemsPerPage}
                    onChange={(e) => {
                      setServicePagination(prev => ({ ...prev, itemsPerPage: parseInt(e.target.value), currentPage: 1 }));
                      loadServices(1);
                    }}
                  >
                    <option value={6}>6</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                    <option value={48}>48</option>
                  </select>
                  <div className="services-count">
                    {servicePagination.totalItems} servicios disponibles
                  </div>
                </div>
              </div>

              {/* Lista de servicios */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="loading-spinner"></div>
                  <p className="mt-4 text-gray-600">Cargando servicios...</p>
                </div>
              ) : services.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Package size={48} className="text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay servicios disponibles</h3>
                  <p className="text-gray-600 text-center">
                    No se encontraron servicios que coincidan con los filtros aplicados
                  </p>
                </div>
              ) : (
                <div className="services-container">
                  {services.map(service => (
                    <div key={service._id} className="service-item-card">
                      <h4 className="service-name">{service.name}</h4>
                      <p className="service-description">{service.description}</p>
                      <div className="service-tags">
                        <span className="service-tag category">
                          {service.category?.name || 'Sin categoría'}
                        </span>
                        <span className="service-tag payment">
                          {service.paymentType === 'ONE_TIME' ? 'Pago único' : 'Recurrente'}
                        </span>
                      </div>
                      
                      <div className="service-footer">
                        <div className="service-price">
                          ${service.price.toLocaleString()}
                        </div>
                        <button
                          onClick={() => addToCart(service)}
                          className="add-button"
                        >
                          <Plus size={12} />
                          Agregar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Paginación de servicios */}
          {servicePagination.totalPages > 1 && (
            <div className="services-pagination">
              <div className="pagination-info">
                <span>
                  Mostrando {((servicePagination.currentPage - 1) * servicePagination.itemsPerPage) + 1} - {Math.min(servicePagination.currentPage * servicePagination.itemsPerPage, servicePagination.totalItems)} de {servicePagination.totalItems} servicios
                </span>
              </div>
              <div className="pagination-controls">
                <button
                  onClick={goToPreviousServicePage}
                  disabled={servicePagination.currentPage === 1}
                  className="btn btn-outline btn-sm"
                >
                  ← Anterior
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: Math.min(5, servicePagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (servicePagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (servicePagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (servicePagination.currentPage >= servicePagination.totalPages - 2) {
                      pageNum = servicePagination.totalPages - 4 + i;
                    } else {
                      pageNum = servicePagination.currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handleServicePageChange(pageNum)}
                        className={`btn btn-sm ${servicePagination.currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={goToNextServicePage}
                  disabled={servicePagination.currentPage === servicePagination.totalPages}
                  className="btn btn-outline btn-sm"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Panel Derecho - Carrito y Resumen */}
        <div className="cart-section">
          {/* Selección de Cliente */}
          <div className="card mb-6 client-selector-container">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <User className="text-purple-600" size={20} />
                Cliente
              </h3>
            </div>
            <div className="card-content">
              <ClientSearchSelect
                value={selectedClient?._id || ''}
                onChange={(value) => {
                  const client = clients.find(c => c._id === value);
                  setSelectedClient(client || null);
                }}
                options={clients.map(client => ({ 
                  value: client._id, 
                  label: client.name 
                }))}
                placeholder="Seleccionar cliente"
                searchPlaceholder="Buscar cliente..."
                maxDisplayItems={5}
              />
              
              {selectedClient && (
                <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-purple-600" />
                    <span className="font-medium text-purple-900">{selectedClient.name}</span>
                  </div>
                  <p className="text-sm text-purple-700 mt-1">{selectedClient.email}</p>
                </div>
              )}
            </div>
          </div>

          {/* Carrito de Compras */}
          <div className="card mb-6">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <ShoppingCart className="text-green-600" size={20} />
                Carrito ({cart.length})
              </h3>
            </div>
            
            <div className="card-content">
              {cart.length === 0 ? (
                <div className="text-center py-6">
                  <ShoppingCart size={40} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">El carrito está vacío</p>
                  <p className="text-xs text-gray-400">Agrega servicios desde la lista</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item, index) => (
                    <div key={item.serviceId} className="cart-item-card">
                      <div className="cart-item-header">
                        <h4 className="cart-item-name">{item.name}</h4>
                        <p className="cart-item-price">${item.price.toLocaleString()} c/u</p>
                      </div>
                      
                      <div className="cart-item-controls">
                        <div className="quantity-controls">
                          <button
                            onClick={() => updateQuantity(item.serviceId, item.quantity - 1)}
                            className="quantity-btn"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="quantity-display">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.serviceId, item.quantity + 1)}
                            className="quantity-btn"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        
                        <div className="cart-item-total">
                          <div className="total-amount">
                            ${(item.price * item.quantity).toLocaleString()}
                          </div>
                          <button
                            onClick={() => removeFromCart(item.serviceId)}
                            className="remove-btn"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Resumen y Acciones */}
          {cart.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title flex items-center gap-2">
                  <DollarSign className="text-green-600" size={20} />
                  Resumen
                </h3>
              </div>
              
              <div className="card-content">
                <div className="space-y-4">
                  {/* Total */}
                  <div className="flex justify-between items-center text-base font-bold text-green-600 border-t pt-2">
                    <span>Total:</span>
                    <span>${calculateTotal().toLocaleString()}</span>
                  </div>

                  {/* Notas */}
                  <div>
                    <label className="form-label">Notas de la venta</label>
                    <textarea
                      value={saleData.notes}
                      onChange={(e) => setSaleData(prev => ({ ...prev, notes: e.target.value }))}
                      className="form-input form-textarea"
                      rows="3"
                      placeholder="Notas adicionales..."
                    />
                  </div>

                  {/* Selector de Estado ELIMINADO - Siempre es PENDING en esta pantalla */}

                  {/* Método de Pago */}
                  <div>
                    <CustomSelect
                      label="Método de Pago"
                      value={saleData.paymentMethod}
                      onChange={(value) => setSaleData(prev => ({ ...prev, paymentMethod: value }))}
                      options={[
                        { value: 'CASH', label: 'Efectivo' },
                        { value: 'CARD', label: 'Tarjeta' },
                        { value: 'TRANSFER', label: 'Transferencia' },
                        { value: 'OTHER', label: 'Otro' }
                      ]}
                      placeholder="Seleccionar método"
                    />
                  </div>

                  {/* Botón procesar */}
                  <button
                    onClick={processSale}
                    disabled={!selectedClient}
                    className="btn btn-primary w-full flex items-center justify-center gap-2 text-sm py-2"
                  >
                    {isEditing ? <Save size={16} /> : (isCloning ? <Copy size={16} /> : <CheckCircle size={16} />)}
                    {isEditing ? 'Guardar Cambios' : (isCloning ? 'Clonar Venta' : 'Crear Borrador')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sistema de notificaciones */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default NewSalePage;